import classNames from "classnames"
import { format, parseISO } from "date-fns"
import pluralize from "pluralize"
import React, { useContext, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"

import Badge from "src/components/Badge"
import Button from "src/components/Button"
import Chips from "src/components/Chips"
import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"
import Tooltip from "src/components/Tooltip"

import { getApideckInvoices, retryInvoiceSync } from "src/api/Apideck"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { titlecase } from "src/utils/string_helpers"

import { AccountingContext } from "./AccountingView"
import { SYNC_STATUSES, SYNC_STATUS_FILTER_OPTIONS } from "./constants"

const { EXCLUDED, FAILED } = SYNC_STATUSES
const GUEST = "Guest"
const INACTIVE = "inactive"

const STATUS_BADGE_COLORS = {
  [SYNC_STATUSES.PENDING]: "blue",
  [SYNC_STATUSES.EXCLUDED]: "yellow",
  [SYNC_STATUSES.FAILED]: "red",
  [SYNC_STATUSES.SUCCESS]: "teal",
}

// Exported for tests: the option list renders in a portal jsdom cannot open.
// A bare label means the counts have not arrived; an explicit (0) means the
// status genuinely has no rows, and those must not look the same.
export const statusOptionLabel = (status, filterOptions) => {
  if (!filterOptions) return titlecase(status)

  return `${titlecase(status)} (${filterOptions.statusCounts?.[status] ?? 0})`
}

const SyncTab = () => {
  const navigate = useNavigate()
  const { integration, marinaSlug } = useContext(AccountingContext)
  const queryClient = useQueryClient()
  const showToast = useToast()

  const table = useDatatable({
    queryKey: ["apideckInvoices", marinaSlug],
    fetchFn: (params) => getApideckInvoices(marinaSlug, params),
    defaultFilters: { search: "", syncStatuses: [] },
    defaultSort: { column: "updatedAt", direction: "desc" },
    enabled: integration.setupComplete,
  })

  const [searchTerm, setSearchTerm] = useState(table.filters.search || "")
  const [debouncedSearch] = useDebounce((value) =>
    table.setFilter("search", value)
  )

  const { syncStatuses } = table.filters
  const filteredCount = table.data?.pagination?.totalCount
  const totalCount = table.data?.filterOptions?.totalCount

  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
    debouncedSearch(event.target.value.trim())
  }

  const clearSearch = () => {
    setSearchTerm("")
    table.setFilter("search", "")
  }

  const resetFilters = () => {
    setSearchTerm("")
    table.clearAllFilters()
  }

  const { mutate: retryInvoice } = useMutation(
    (id) => retryInvoiceSync({ marinaSlug, id }),
    {
      onSuccess: (data) => {
        const { invoice: updatedInvoice } = data
        showToast(
          `Retry of invoice #${updatedInvoice.billingInvoiceId} initiated successfully`,
          { type: "success" }
        )
        // A retry moves the record back to pending, so refetch rather than
        // patching the row — the filtered set itself may have changed.
        queryClient.invalidateQueries(["apideckInvoices", marinaSlug])
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const COLUMNS = [
    {
      key: "syncStatus",
      header: "Status",
      sortable: true,
      cardRole: "badge",
      width: "8%",
      render: (row) => (
        <Badge
          color={STATUS_BADGE_COLORS[row.syncStatus] || "gray"}
          text={titlecase(row.syncStatus)}
        />
      ),
    },
    {
      key: "invoice",
      header: "Invoice",
      cardRole: "title",
      width: "20%",
      render: (row) => (
        <a
          href={row.parentObjectPath}
          className="cursor-pointer text-blue-600"
          rel="noreferrer noopener"
          target="_blank"
        >
          <span className="block truncate">{row.billingInvoiceId}</span>
        </a>
      ),
    },
    {
      key: "contact",
      header: "Customer",
      sortable: true,
      cardRole: "subtitle",
      width: "13%",
      render: (row) => row.contact?.name || GUEST,
    },
    {
      key: "updatedAt",
      header: "Updated",
      sortable: true,
      cardRole: "detail",
      width: "13%",
      render: (row) => (
        <span className="block truncate">
          {format(parseISO(row.updatedAt), "MM/dd/yyyy hh:mm aa")}
        </span>
      ),
    },
    {
      key: "syncedAt",
      header: "Last Synced",
      sortable: true,
      cardRole: "detail",
      width: "13%",
      render: (row) =>
        row.syncedAt ? (
          <span className="block truncate">
            {format(parseISO(row.syncedAt), "MM/dd/yyyy hh:mm aa")}
          </span>
        ) : null,
    },
    {
      key: "amount",
      header: "Amount",
      sortable: true,
      cardRole: "detail",
      width: "8%",
      render: (row) => formatCurrencyFromMinorUnits({ amount: row.amount }),
    },
    {
      key: "errors",
      header: "",
      cardRole: "detail",
      width: "15%",
      render: (row) => {
        const errorCount = row.syncErrors.length
        if (errorCount === 0) return undefined

        const actionable = row.syncErrors.filter((error) => error.actionable)
        const nonActionable = row.syncErrors.filter(
          (error) => !error.actionable
        )
        // Headings only earn their space when both kinds are present — otherwise the count beside
        // the icon already says which kind this row is, and a lone heading is just noise.
        const grouped = actionable.length > 0 && nonActionable.length > 0
        const bulleted = errorCount > 1

        const errorLines = (errors) =>
          errors.map((error, index) => (
            <div key={index}>{`${bulleted ? "- " : ""}${error.code}: ${
              error.displayMessage
            }`}</div>
          ))

        return (
          <div className="flex items-center whitespace-nowrap font-semibold text-red-600">
            <Tooltip
              text={
                <div className="space-y-3">
                  {grouped ? (
                    <>
                      <div>
                        <div className="font-semibold">Action required</div>
                        {errorLines(actionable)}
                      </div>
                      <div>
                        <div className="font-semibold">No action needed</div>
                        {errorLines(nonActionable)}
                      </div>
                    </>
                  ) : (
                    errorLines(row.syncErrors)
                  )}
                </div>
              }
              variant="dark"
              placement="bottom"
              maxWidth="400px"
              tabIndex={0}
            >
              <i className="fa-solid fa-circle-exclamation pr-1 text-base font-semibold" />
            </Tooltip>
            <div className="block truncate">
              {actionable.length > 0
                ? `${actionable.length} ${pluralize(
                    "action",
                    actionable.length
                  )} required`
                : `${errorCount} sync ${pluralize("error", errorCount)}`}
            </div>
          </div>
        )
      },
    },
  ]

  const ACTIONS = [
    {
      label: "Retry",
      icon: "replay",
      primary: true,
      onClickSingle: (row) => retryInvoice(row.id),
      disabled: (row) =>
        integration.status === INACTIVE ||
        (row.syncStatus !== FAILED && row.syncStatus !== EXCLUDED),
    },
  ]

  const renderStatusFilter = () => (
    <Select
      multiple
      value={syncStatuses}
      onValueChange={(values) => table.setFilter("syncStatuses", values)}
    >
      <SelectTrigger
        variant="button"
        className="w-full sm:w-auto"
        aria-label="Status"
      >
        Status
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {SYNC_STATUS_FILTER_OPTIONS.map((status) => (
          <SelectItem key={status} value={status}>
            {statusOptionLabel(status, table.data?.filterOptions)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderFilterChips = () => (
    <div className="flex flex-wrap">
      {table.filters.search && (
        <Chips.Chip
          text={`Search: ${table.filters.search}`}
          onClick={clearSearch}
        />
      )}
      {syncStatuses.map((status) => (
        <Chips.Chip
          key={status}
          text={`Status: ${titlecase(status)}`}
          onClick={() =>
            table.setFilter(
              "syncStatuses",
              syncStatuses.filter((current) => current !== status)
            )
          }
        />
      ))}
      {table.activeFilters.length > 0 && (
        <div className="flex w-min items-center">
          <Button variant="ghost" small onClick={resetFilters}>
            Reset
          </Button>
        </div>
      )}
    </div>
  )

  const renderRecordCount = () => {
    if (totalCount === undefined || filteredCount === undefined) return null

    return (
      <div className="text-muted text-sm">
        {`Showing ${filteredCount} of ${totalCount} ${pluralize(
          "record",
          totalCount
        )}`}
      </div>
    )
  }

  const renderEmptyState = (variant) => {
    let title
    let subtitle

    if (variant === "incompleteSetup") {
      title = "Syncing not yet started"
      subtitle =
        "Please complete setup and ensure your integration status is 'on'."
    } else if (variant === "error") {
      title = "Error loading invoices"
      subtitle =
        "Please try reloading the page or contact Dockwa support at mayday@dockwa.com"
    }

    return (
      <div className="card my-5 flex min-h-96 flex-col justify-center text-center">
        <div
          className={classNames(
            { "text-red-600": variant === "error" },
            "font-bold"
          )}
        >
          {title}
        </div>
        <div className="text-muted text-sm">{subtitle}</div>
        {variant === "incompleteSetup" ? (
          <div className="mt-2">
            <Button variant="primary" onClick={() => navigate("/setup")}>
              Go to Setup
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  const renderTable = () => {
    if (!integration.setupComplete) return renderEmptyState("incompleteSetup")
    if (table.isError) return renderEmptyState("error")

    return (
      <DataTableV2
        name="apideck-sync-details"
        columns={COLUMNS}
        data={table.records}
        actions={ACTIONS}
        maxHeight="none"
        emptyState={
          <span className="text-gray-500">
            {table.activeFilters.length > 0
              ? "There are no invoices that match your filters, please try adjusting them."
              : "There are no synced invoices available for review at this time."}
          </span>
        }
        {...table.tableProps}
      />
    )
  }

  return (
    <div className="mb-[250px]">
      <h4 className="mb-1 pt-6 font-semibold text-gray-900">Sync Details</h4>
      <div className="mb-8">
        <span className="text-gray-800">Log of recent sync activity.</span>
      </div>
      <div className="flex flex-wrap xl:flex-nowrap">
        <div className="w-full pb-2 xl:mr-2 xl:w-[610px] xl:p-0">
          <Form.IconTextField
            icon={<i className="fa-kit fa-search-mdi text-xl text-gray-400" />}
            id="invoice-item-search"
            isClearable
            onChange={handleSearch}
            onClearSelection={clearSearch}
            position="left"
            value={searchTerm}
            placeholder="Search by customer name/email or the ID of an invoice, reservation, contract, or sale"
          />
        </div>
        <div className="flex w-full xl:w-min">{renderStatusFilter()}</div>
      </div>
      <div className="px-4 xl:px-0">
        <Chips.List>{renderFilterChips()}</Chips.List>
      </div>
      <div className="mt-4 px-4 xl:px-0">{renderRecordCount()}</div>
      <div className="my-4">{renderTable()}</div>
    </div>
  )
}

export default SyncTab
